import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { HERO_PRODUCTS } from "./heroProducts";

/** Which breakpoint a product first appears at: phones 3, tablets 4, large desktop all 5. */
const VISIBILITY = { base: "", sm: "hidden sm:block", xl: "hidden xl:block" };

const IMG_SHADOW =
  "[filter:drop-shadow(0_0_0_rgba(59,130,246,0))_drop-shadow(0_18px_18px_rgba(2,10,30,0.5))] group-hover:[filter:drop-shadow(0_0_26px_rgba(59,130,246,0.7))_drop-shadow(0_18px_18px_rgba(2,10,30,0.4))] group-focus-visible:[filter:drop-shadow(0_0_26px_rgba(59,130,246,0.7))_drop-shadow(0_18px_18px_rgba(2,10,30,0.4))]";

/**
 * The entrance animation on the wrapping <motion.div> below runs on its own fixed timer
 * (opacity/transform reach their final "settled" values on a wall-clock schedule) — it does
 * NOT wait for this <img> to actually finish downloading and decoding. On a fast/cached load
 * the two finish at roughly the same time and it looks like one smooth reveal. On a slower or
 * cold-cache load the wrapper settles first and the image — which has no transition of its
 * own — then pops in abruptly moments later once the network delivers it, which reads as "the
 * hero settled, then changed again". How long that gap is depends purely on network/cache
 * timing, which is why it looked intermittent rather than happening on every load.
 * This gates the image's own opacity on its real `load` event (with a `complete` check for the
 * case where it was already cached before this ran, so that fast path is not delayed either)
 * so a slow image now fades in gently instead of snapping in — same end state, same duration/
 * position/animation timing as before, just no more silent second change after the reveal.
 */
function ProductImage({ src, alt, width, height, loading, fetchPriority, className }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) setLoaded(true);
  }, []);
  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      width={width}
      height={height}
      decoding="async"
      loading={loading}
      fetchPriority={fetchPriority}
      draggable="false"
      onLoad={() => setLoaded(true)}
      className={`${className} ${loaded ? "opacity-100" : "opacity-0"}`}
    />
  );
}

/**
 * Editorial product composition for the homepage hero: real Shingare products from different families at deliberately
 * different scales (tube cleaning dominant, then pipe beveling, then torque / expansion / machining), each linking to
 * its real category page. Everything is a transparent cutout of a genuine product photo — see heroProducts.js.
 *
 * Motion (skipped for prefers-reduced-motion): staggered rise-in, a slow idle drift per product, and a very small
 * pointer parallax on mouse devices. Hover/focus: the product lifts slightly, picks up a blue glow, and its label
 * swaps to "Explore … →".
 */
/** Merges Admin → Home Page → Hero Showcase text/link/image overrides (by `id`) onto the fixed layout/position data
 * in heroProducts.js. Layout is never editable from Admin — only what each tile says and links to. */
function withOverrides(items) {
  if (!items?.length) return HERO_PRODUCTS;
  const byId = new Map(items.map((o) => [o.id, o]));
  return HERO_PRODUCTS.map((p) => {
    const o = byId.get(p.id);
    if (!o) return p;
    return {
      ...p,
      name: o.name || p.name,
      eyebrow: o.eyebrow || p.eyebrow,
      cta: o.cta || p.cta,
      href: o.href || p.href,
      image: o.image || p.image,
      alt: o.alt || p.alt,
    };
  });
}

export default function HeroProductShowcase({ items }) {
  const reduce = useReducedMotion();
  const stageRef = useRef(null);
  const products = withOverrides(items);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || reduce) return undefined;
    let raf = 0;
    const move = (e) => {
      if (e.pointerType !== "mouse") return;
      const r = el.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
      const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { el.style.setProperty("--px", x.toFixed(3)); el.style.setProperty("--py", y.toFixed(3)); });
    };
    const leave = () => { el.style.setProperty("--px", "0"); el.style.setProperty("--py", "0"); };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerleave", leave);
    return () => { cancelAnimationFrame(raf); el.removeEventListener("pointermove", move); el.removeEventListener("pointerleave", leave); };
  }, [reduce]);

  return (
    <div ref={stageRef} className="relative mx-auto aspect-[1/0.8] w-full max-w-[560px] sm:aspect-[1.2/1] sm:max-w-[600px] md:aspect-[1.06/1] md:max-w-none lg:aspect-[1.12/1]" data-testid="hero-product-showcase">
      {/* stage lighting: soft blue light behind the primary product, faint technical rings, a floor glow */}
      <div className="pointer-events-none absolute -inset-8 bg-[radial-gradient(ellipse_52%_46%_at_38%_58%,rgba(23,105,255,0.34),transparent_72%)]" aria-hidden="true" />
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <g fill="none" stroke="rgba(127,176,255,0.13)" strokeWidth="0.25" vectorEffect="non-scaling-stroke">
          <circle cx="40" cy="60" r="34" />
          <circle cx="40" cy="60" r="50" strokeDasharray="0.6 1.4" />
          <circle cx="40" cy="60" r="68" stroke="rgba(127,176,255,0.07)" />
        </g>
      </svg>
      <div className="pointer-events-none absolute inset-x-[2%] bottom-[2%] h-[16%] rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.22),transparent_70%)]" aria-hidden="true" />

      <ul className="absolute inset-0 m-0 list-none p-0" aria-label="Shingare product families">
        {products.map((p, i) => (
          <li
            key={p.id}
            className={`absolute left-[var(--ml,0%)] top-[var(--mt,0%)] w-[var(--mw,30%)] sm:left-[var(--l)] sm:top-[var(--t)] sm:w-[var(--w)] ${VISIBILITY[p.show]}`}
            style={{ "--l": `${p.l}%`, "--t": `${p.t}%`, "--w": `${p.width}%`, "--ml": `${p.ml}%`, "--mt": `${p.mt}%`, "--mw": `${p.mw}%`, zIndex: p.z }}
          >
            {/* layer 1 — pointer parallax */}
            <div style={reduce ? undefined : { transform: `translate3d(calc(var(--px, 0) * ${p.depth} * -11px), calc(var(--py, 0) * ${p.depth} * -8px), 0)`, transition: "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)" }}>
              {/* layer 2 — entrance */}
              <motion.div
                initial={reduce ? false : { opacity: 0, y: 34, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.95, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* layer 3 — idle drift */}
                <motion.div
                  animate={reduce ? undefined : { y: [0, -7, 0] }}
                  transition={reduce ? undefined : { duration: p.float, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }}
                >
                  <Link
                    to={p.href}
                    aria-label={`${p.name}. ${p.cta}`}
                    data-testid={`hero-product-${p.id}`}
                    className="group relative block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82FF] focus-visible:ring-offset-4 focus-visible:ring-offset-[#071B3A]"
                  >
                    <span className="pointer-events-none absolute inset-x-[10%] bottom-[-2%] -z-10 h-[9%] rounded-[50%] bg-[#020a1e]/70 blur-[12px]" aria-hidden="true" />
                    <ProductImage
                      src={p.image}
                      alt={p.alt}
                      width={p.w}
                      height={p.h}
                      loading={i === 0 ? "eager" : p.show === "base" ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : undefined}
                      className={`block h-auto w-full origin-bottom transition-[transform,filter,opacity] duration-500 ease-out group-hover:scale-[1.045] group-focus-visible:scale-[1.045] ${IMG_SHADOW}`}
                    />
                    {/* label: small and quiet; on hover the second line swaps to the call to action */}
                    <span className={`pointer-events-none absolute top-full z-10 mt-1.5 flex flex-col gap-0.5 whitespace-nowrap xl:mt-2 ${p.label === "left" ? "left-[3%] items-start text-left" : "right-[1%] items-end text-right"} ${p.quiet ? "opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100" : ""}`}>
                      <span className="hidden items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#7FB0FF] xl:flex">
                        <span className="h-px w-3 bg-[#3B82FF]" aria-hidden="true" />
                        {p.eyebrow}
                      </span>
                      {/* both texts share one grid cell, so the box is always as wide as the longer one (no clipping) */}
                      <span className={`grid h-[17px] overflow-hidden text-[11px] font-medium sm:text-[12.5px] xl:text-[13px] ${p.label === "left" ? "justify-items-start" : "justify-items-end"}`}>
                        <span className="col-start-1 row-start-1 text-white/85 transition-all duration-300 group-hover:-translate-y-full group-hover:opacity-0 group-focus-visible:-translate-y-full group-focus-visible:opacity-0">{p.name}</span>
                        <span className="col-start-1 row-start-1 hidden translate-y-full text-[#9CC3FF] opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100 sm:block">{p.cta} →</span>
                      </span>
                    </span>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
